import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { ErrorComponent } from './core/components/error/error.component';
import { NotFoundComponent } from './core/components/not-found/not-found.component';
import { NotSupportedComponent } from './core/components/not-supported/not-supported.component';
import { AnalyticsPagesEnum } from './core/constants/analytics-pages';
import { AuthGuard } from './core/guards/auth.guard';
import { BrowserGuard } from './core/guards/browser.guard';
import { DashboardGuard } from './core/guards/dashboard.guard';
import { DemoModeGuard } from './core/guards/demo-mode.guard';
import { NotAuthGuard } from './core/guards/not-auth.guard';
import { PermissionsGuard } from './core/guards/permissions.guard';
import { SubToSubReferralInvitesGuard } from './core/guards/sub-to-sub-referral-invites.guard';
import { EarlyPaySubscriptionComponent } from './early-pay-requests/early-pay-subscription/early-pay-subscription.component';
import { EarlyPayCalculatorPageComponent } from './ui/epp/early-pay-calculator-page/early-pay-calculator-page/early-pay-calculator-page.component';
import { RoiCalculatorComponent } from './ui/epp/early-pay-calculator-page/early-pay-roi-calculator-page/roi-calculator.component';
import { EarlyPayPlatformCalculatorComponent } from './ui/epp/early-pay-platform-calculator/early-pay-platform-calculator.component';
import { FullPagePromoBannerComponent } from './ui/full-page-promo-banner/full-page-promo-banner.component';
import { NylasIntegrationBannerComponent } from './ui/nylas-integration-banner/nylas-integration-banner.component';
import { UnskippableBannerComponent } from './ui/unskippable-banner/unskippable-banner.component';

const routes: Routes = [
  {
    path: 'early-pay-calculator',
    component: EarlyPayCalculatorPageComponent,
  },
  {
    path: 'subscription-calculator',
    component: EarlyPayPlatformCalculatorComponent,
  },
  {
    path: 'subscription',
    component: EarlyPaySubscriptionComponent,
  },
  {
    path: 'early-pay-roi-calculator',
    component: RoiCalculatorComponent,
  },
  {
    path: '',
    canActivate: [BrowserGuard, AuthGuard],
    children: [
      {
        path: 'user',
        loadChildren: () => import('./user/user.module').then((m) => m.UserModule),
        canActivate: [DemoModeGuard],
      },
      {
        path: 'early-pay',
        loadChildren: () =>
          import('./early-pay-requests/early-pay-requests.module').then((m) => m.EarlyPayRequestsModule),
        canActivate: [DemoModeGuard],
        data: { analyticsPage: AnalyticsPagesEnum.EARLY_PAY },
      },
      {
        path: 'early-pay-requests', // Legacy URL
        redirectTo: 'early-pay',
      },
      {
        path: 'certificates-of-insurance',
        loadChildren: () => import('./coi-general-contractor/certificates.module').then((m) => m.CertificatesModule),
        data: { analyticsPage: AnalyticsPagesEnum.CERTIFICATES_OF_INSURANCE },
      },
      {
        path: 'subcontractor-insurance',
        loadChildren: () =>
          import('./coi-subcontractor/subcontractor-insurance.module').then((m) => m.SubcontractorInsuranceModule),
        canActivate: [DemoModeGuard],
        data: { analyticsPage: AnalyticsPagesEnum.SUBCONTRACTOR_INSURANCE },
      },
      {
        path: 'projects',
        loadChildren: () => import('./projects/projects.module').then((m) => m.ProjectsModule),
        data: { analyticsPage: AnalyticsPagesEnum.PROJECTS },
      },
      {
        path: 'contracts',
        loadChildren: () => import('./contracts/contracts.module').then((m) => m.ContractsModule),
        canActivate: [PermissionsGuard, DemoModeGuard],
        data: {
          permissions: ['constrafor.view_epp_contracts', 'constrafor.can_view_contract_tab'],
          analyticsPage: AnalyticsPagesEnum.CONTRACTS,
        },
      },
      {
        path: 'invoicing',
        loadChildren: () => import('./invoicing/invoicing.module').then((m) => m.InvoicingModule),
        canActivate: [PermissionsGuard, DemoModeGuard],
        data: {
          permissions: ['constrafor.can_view_invoice_tab'],
          analyticsPage: AnalyticsPagesEnum.INVOICING,
        },
      },
      {
        path: 'dashboard',
        loadChildren: () => import('./dashboard/dashboard.module').then((m) => m.DashboardModule),
        canActivate: [PermissionsGuard, DashboardGuard, SubToSubReferralInvitesGuard],
        data: {
          permissions: ['constrafor.can_view_dashboard_tab'],
          analyticsPage: AnalyticsPagesEnum.DASHBOARD,
        },
      },
      {
        path: 'cfo',
        loadChildren: () => import('./cfo/cfo.routes').then((m) => m.cfoRoutes),
        data: { analyticsPage: AnalyticsPagesEnum.CFO },
      },
      {
        path: 'rewards',
        loadChildren: () => import('./rewards/rewards.module').then((m) => m.RewardsModule),
        data: { analyticsPage: AnalyticsPagesEnum.REWARDS },
      },
      {
        path: '',
        loadChildren: () => import('./home/home.module').then((m) => m.HomeModule),
        data: { analyticsPage: AnalyticsPagesEnum.HOME },
      },
      {
        path: 'diversity',
        loadChildren: () => import('./diversity/diversity.module').then((m) => m.DiversityModule),
        canActivate: [DemoModeGuard],
        data: { analyticsPage: AnalyticsPagesEnum.DIVERSITY },
      },
      {
        path: 'diversity-tracking',
        loadChildren: () =>
          import('./diversity-tracking/diversity-tracking.module').then((m) => m.DiversityTrackingModule),
        canActivate: [PermissionsGuard],
        data: {
          permissions: ['constrafor.can_view_diversity_tracking_tab'],
          analyticsPage: AnalyticsPagesEnum.DIVERSITY_TRACKING,
        },
      },
      {
        path: 'company-profile',
        loadChildren: () => import('./company-profile/company-profile.module').then((m) => m.CompanyProfileModule),
        canActivate: [DemoModeGuard],
        data: { analyticsPage: AnalyticsPagesEnum.COMPANY_PROFILE },
      },
      {
        path: 'prequal',
        loadChildren: () => import('./prequal/prequal.module').then((m) => m.PrequalModule),
        canActivate: [PermissionsGuard, DemoModeGuard],
        data: {
          permissions: ['constrafor.can_view_prequal_tab'],
          analyticsPage: AnalyticsPagesEnum.PREQUAL,
        },
      },
      {
        path: 'lender-portal',
        loadChildren: () => import('./lender-portal/lender-portal.module').then((m) => m.LenderPortalModule),
        data: { analyticsPage: AnalyticsPagesEnum.LENDER_PORTAL },
      },
      {
        path: 'financials',
        loadChildren: () => import('./financials/financials.module').then((m) => m.FinancialsModule),
        canActivate: [DemoModeGuard],
        data: { analyticsPage: AnalyticsPagesEnum.FINANCIALS },
      },
      {
        path: 'file-storage',
        loadChildren: () => import('./file-storage/file-storage.module').then((m) => m.FileStorageModule),
        canActivate: [DemoModeGuard],
        data: { analyticsPage: AnalyticsPagesEnum.FILE_STORAGE },
      },
      {
        path: 'contractors',
        loadChildren: () => import('./contacts/contacts.module').then((m) => m.ContactsModule),
        canActivate: [DemoModeGuard],
        data: { analyticsPage: AnalyticsPagesEnum.CONTRACTORS },
      },
      {
        path: 'pre-approval',
        loadChildren: () => import('./pre-approval/pre-approval.module').then((m) => m.PreApprovalModule),
        data: { analyticsPage: AnalyticsPagesEnum.PRE_APPROVAL },
      },
      {
        path: 'full-page-promo-banner',
        component: FullPagePromoBannerComponent,
      },
      {
        path: 'connect-nylas-promo',
        component: NylasIntegrationBannerComponent,
      },
      {
        path: 'unskippable-banner',
        component: UnskippableBannerComponent,
      },
    ],
  },
  {
    path: 'auth',
    canActivate: [NotAuthGuard, BrowserGuard],
    loadChildren: () => import('./auth/auth.module').then((m) => m.AuthModule),
  },
  {
    path: 'not-supported',
    component: NotSupportedComponent,
  },
  {
    path: 'error',
    component: ErrorComponent,
  },
  {
    path: '**',
    component: NotFoundComponent,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { anchorScrolling: 'enabled', paramsInheritanceStrategy: 'emptyOnly' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
